// Screen-size helpers for phones and tablets. Pure functions: no screen code, no browser calls.
//
// Phone:   narrower than 640px
// Tablet:  640px up to (not including) 1024px
// Desktop: 1024px and wider (the layout the app always had)
//
// The style sheet in App.js uses the same two numbers (639px and 1023px) in its media queries, so a
// screen looks the same whether a rule comes from here or from the style sheet.

export const PHONE_MAX = 640;   // width below this is a phone
export const TABLET_MAX = 1024; // width below this (and not a phone) is a tablet

// "phone", "tablet" or "desktop" for a window width in pixels. A missing or odd width counts as desktop.
export function deviceFor(width) {
  if (typeof width !== "number" || !isFinite(width) || width <= 0) return "desktop";
  if (width < PHONE_MAX) return "phone";
  if (width < TABLET_MAX) return "tablet";
  return "desktop";
}

// Phones and tablets use the bottom bar. Desktop keeps the sidebar.
export function usesBottomNav(device) {
  return device === "phone" || device === "tablet";
}

// The tabs that always sit in the bottom bar, in order. Anything else goes under "More".
// Not every business has every tab (Expense Management is for QuickBooks businesses), so a missing one is skipped
// and the bar is topped up from the backup list so it still has four buttons plus More.
export const BAR_TAB_KEYS = ["dashboard", "detail", "inbox", "chat"];
export const BAR_BACKUP_KEYS = ["clients", "reports", "estimator"];
export const BAR_SIZE = 4;

// Short, plain labels for the narrow bar. The "More" sheet shows the full names.
export const BAR_LABELS = {
  dashboard: "Home",
  detail: "Jobs",
  inbox: "Expenses",
  chat: "AI",
  clients: "Clients",
  reports: "Reports",
  estimator: "Quotes",
};

export function barLabel(tab) {
  return BAR_LABELS[tab.key] || tab.label;
}

// Splits the full tab list into what shows in the bottom bar and what goes in the More sheet.
// Keeps the original order inside each group. Never drops or repeats a tab.
export function splitNavTabs(tabs) {
  const list = Array.isArray(tabs) ? tabs : [];
  const has = (k) => list.some((t) => t.key === k);
  const keys = BAR_TAB_KEYS.filter(has);
  for (const k of BAR_BACKUP_KEYS) {
    if (keys.length >= BAR_SIZE) break;
    if (has(k) && !keys.includes(k)) keys.push(k);
  }
  const bar = keys.map((k) => list.find((t) => t.key === k));
  const more = list.filter((t) => !keys.includes(t.key));
  return { bar, more };
}

// True when the open tab lives in the More sheet, so the More button can show as selected.
export function moreIsActive(more, activeKey) {
  return (more || []).some((t) => t.key === activeKey);
}

// Phone job list: the sort choices for the stacked cards (the table has clickable column headings instead).
export const JOB_SORT_CHOICES = [
  { key: "profit", label: "Profit" },
  { key: "revenue", label: "Revenue" },
  { key: "margin", label: "Margin" },
  { key: "costs", label: "Costs" },
  { key: "name", label: "Job name" },
  { key: "client", label: "Client" },
];
